<template>
  <div class="uidmanual-root">
    <div v-if="booting" class="page">
      <div class="brand">{{ brandTitle }}</div>
      <div class="sub">加载中…</div>
    </div>
    <div v-show="!booting" class="page">
      <div class="brand">{{ brandTitle }}</div>
      <div class="sub">{{ brandSub }}</div>
      <div class="card">
        <div class="amount-row">
          <span class="currency">¥</span>
          <span class="amount">{{ amountYuan }}</span>
          <button type="button" class="btn-copy" @click="copyText(copyAmountValue)">复制</button>
        </div>
        <p class="amount-hint">{{ amountHint }}</p>
        <div v-if="countdownVisible" class="countdown-row">
          订单剩余 <span class="countdown-val">{{ countdownText }}</span>
        </div>
        <p v-if="statusHint" class="hint" :class="{ err: isErrHint }">{{ statusHint }}</p>
        <div v-show="canPay">
          <div v-if="scanPay">
            <img v-if="qrImage" class="scan-qr" :src="qrImage" alt="收款码" />
            <p class="scan-copy">请截图保存二维码，打开支付宝，使用扫一扫完成付款</p>
            <a v-if="mobileScan" class="btn-scan" :href="scanJumpUrl">打开扫一扫</a>
          </div>
          <button v-else type="button" class="btn-jump" :disabled="jumping" @click="onJump">打开支付宝付款</button>
          <div v-if="showManualAccount" class="account-fallback">
            <p class="account-fallback-tip">如果自动跳转过不去，就复制支付宝账号手动转账。</p>
            <div class="account-fallback-row">
              <span class="account-fallback-no">{{ manualAlipayAccount }}</span>
              <button type="button" class="btn-copy" @click="copyText(manualAlipayAccount)">复制</button>
            </div>
          </div>
        </div>
        <div class="meta">
          <div class="meta-row"><span>商户订单号</span><span class="meta-val">{{ mchOrderNo || '—' }}</span></div>
          <div class="meta-row"><span>下单时间</span><span class="meta-val">{{ createTime || '—' }}</span></div>
        </div>
      </div>
    </div>
    <div v-show="toastVisible" class="toast">{{ toastText }}</div>
    <div v-if="showPayerNameModal" class="payer-name-mask" role="dialog" aria-modal="true">
      <div class="payer-name-dialog">
        <h3 class="payer-name-title">请填写真实姓名</h3>
        <p class="payer-name-text">请输入付款人<strong>真实姓名</strong>（支付宝实名，不是昵称）</p>
        <input
          v-model="payerNameInput"
          class="payer-name-input"
          type="text"
          maxlength="32"
          autocomplete="off"
          placeholder="例如：张三"
          @keyup.enter="onConfirmPayerName"
        />
        <button type="button" class="payer-name-confirm" :disabled="payerNameSubmitting" @click="onConfirmPayerName">确定</button>
        <p v-if="payerNameError" class="payer-name-err">{{ payerNameError }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { executeUnifiedPay, fetchUidManualCashierPage, submitUidManualPayerName } from '@/api/api'

const STYLE_ID = 'uidmanual-pay-stylesheet'
const POLL_MS = 3000
const AMOUNT_HINT = '请按页面金额付款，付款人须与支付宝实名一致'

function formatMmSs (remainSec) {
  const s = Math.max(0, Math.floor(remainSec))
  const m = Math.floor(s / 60)
  const r = s % 60
  return String(m).padStart(2, '0') + ':' + String(r).padStart(2, '0')
}

export default {
  name: 'UidManualPay',
  data () {
    return {
      booting: true,
      orderId: '',
      token: '',
      brandTitle: '支付宝转账',
      brandSub: '请先填写真实姓名，再打开支付宝付款',
      amountYuan: '--',
      amountHint: AMOUNT_HINT,
      mchOrderNo: '',
      createTime: '',
      hint: '',
      failReason: '',
      pageLoadFailed: false,
      invalidToken: false,
      expired: false,
      orderPaid: false,
      expireAtMillis: null,
      nowMs: Date.now(),
      payerNameFilled: false,
      scanPay: false,
      qrImage: '',
      scanJumpUrl: '',
      manualAlipayAccount: '',
      mobile: false,
      scanReady: false,
      scanEnsuring: false,
      scanTries: 0,
      payerNameInput: '',
      payerNameSubmitting: false,
      payerNameError: '',
      jumping: false,
      toastVisible: false,
      toastText: '',
      toastTimer: null,
      tickTimer: null,
      pollTimer: null
    }
  },
  computed: {
    copyAmountValue () {
      return this.amountYuan && this.amountYuan !== '--' ? this.amountYuan : ''
    },
    remainSec () {
      if (!this.expireAtMillis) return 0
      return Math.max(0, Math.floor((Number(this.expireAtMillis) - this.nowMs) / 1000))
    },
    countdownText () {
      return formatMmSs(this.remainSec)
    },
    countdownVisible () {
      return !this.invalidToken && !this.orderPaid && this.expireAtMillis != null && this.remainSec > 0
    },
    canPay () {
      return !this.invalidToken && !this.expired && !this.orderPaid && this.payerNameFilled
    },
    mobileScan () {
      return this.mobile && !!this.scanJumpUrl
    },
    showManualAccount () {
      return !this.scanPay && !!String(this.manualAlipayAccount || '').trim()
    },
    showPayerNameModal () {
      return !this.booting && !this.invalidToken && !this.expired && !this.orderPaid && !this.payerNameFilled
    },
    isErrHint () {
      return this.invalidToken || this.expired || this.pageLoadFailed
    },
    statusHint () {
      if (this.invalidToken || this.pageLoadFailed) {
        return (this.failReason && String(this.failReason).trim()) || '链接无效或已失效，请重新下单'
      }
      if (this.orderPaid) return '支付成功，可关闭本页'
      if (this.expired) return '订单已过期'
      if (!this.payerNameFilled) return '请先填写付款人真实姓名'
      return this.hint || ''
    }
  },
  mounted () {
    document.title = '支付宝转账'
    this.mobile = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || '')
    this.ensureStyle()
    this.orderId = String((this.$route.params && this.$route.params.orderId) || '')
    this.token = String((this.$route.query && this.$route.query.t) || '')
    this.boot()
  },
  beforeDestroy () {
    this.clearTimers()
    if (this.toastTimer) {
      clearTimeout(this.toastTimer)
      this.toastTimer = null
    }
  },
  methods: {
    clearTimers () {
      if (this.tickTimer) {
        clearInterval(this.tickTimer)
        this.tickTimer = null
      }
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    ensureStyle () {
      if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return
      const el = document.createElement('style')
      el.id = STYLE_ID
      el.textContent = `
.uidmanual-root { min-height: 100vh; background: #f5f5f5; }
.uidmanual-root .page { max-width: 420px; margin: 0 auto; padding: 24px 16px 40px; box-sizing: border-box; }
.uidmanual-root .brand { font-size: 18px; font-weight: 600; color: #333; text-align: center; }
.uidmanual-root .sub { font-size: 13px; color: #999; text-align: center; margin-top: 6px; }
.uidmanual-root .card { background: #fff; border-radius: 12px; padding: 24px 20px; margin-top: 20px; }
.uidmanual-root .amount-row { display: flex; align-items: center; justify-content: center; gap: 10px; flex-wrap: wrap; }
.uidmanual-root .currency { font-size: 20px; }
.uidmanual-root .amount { font-size: 36px; font-weight: 600; }
.uidmanual-root .btn-copy { height: 32px; padding: 0 12px; border: 0; border-radius: 6px; background: #1677ff; color: #fff; font-size: 13px; cursor: pointer; }
.uidmanual-root .amount-hint { text-align: center; color: #f56c6c; font-size: 13px; line-height: 1.5; margin: 12px 0 0; }
.uidmanual-root .countdown-row { text-align: center; margin-top: 10px; color: #666; font-size: 13px; }
.uidmanual-root .countdown-val { color: #1677ff; font-weight: 600; }
.uidmanual-root .hint { text-align: center; color: #666; font-size: 13px; line-height: 1.5; margin: 14px 0; }
.uidmanual-root .hint.err { color: #f56c6c; }
.uidmanual-root .btn-jump { display: block; width: 100%; margin-top: 22px; height: 44px; line-height: 44px; border: 0; border-radius: 8px; background: #1677ff; color: #fff; font-size: 16px; cursor: pointer; text-align: center; text-decoration: none; box-sizing: border-box; }
.uidmanual-root .btn-jump:disabled { opacity: 0.5; cursor: not-allowed; }
.uidmanual-root .scan-qr { display: block; width: min(72vw, 240px); height: auto; margin: 8px auto 0; }
.uidmanual-root .scan-copy { text-align: center; color: #333; font-size: 14px; line-height: 1.6; margin: 12px 0 0; }
.uidmanual-root .btn-scan { display: block; width: 100%; margin-top: 16px; height: 44px; line-height: 44px; text-align: center; text-decoration: none; border-radius: 8px; background: #1677ff; color: #fff; font-size: 16px; }
.uidmanual-root .meta { margin-top: 18px; font-size: 12px; color: #999; }
.uidmanual-root .meta-row { display: flex; justify-content: space-between; padding: 4px 0; gap: 12px; }
.uidmanual-root .meta-val { color: #666; max-width: 60%; word-break: break-all; text-align: right; }
.uidmanual-root .account-fallback { margin-top: 18px; text-align: center; }
.uidmanual-root .account-fallback-tip { margin: 0; color: #f56c6c; font-size: 15px; font-weight: 600; line-height: 1.5; }
.uidmanual-root .account-fallback-row { display: flex; align-items: center; justify-content: center; gap: 10px; margin-top: 10px; flex-wrap: wrap; }
.uidmanual-root .account-fallback-no { font-size: 22px; font-weight: 700; color: #333; word-break: break-all; }
.uidmanual-root .toast { position: fixed; left: 50%; bottom: 80px; transform: translateX(-50%); background: rgba(0,0,0,0.72); color: #fff; font-size: 13px; padding: 8px 16px; border-radius: 16px; z-index: 9; }
.uidmanual-root .payer-name-mask { position: fixed; inset: 0; z-index: 1000; background: rgba(0,0,0,0.45); display: flex; align-items: center; justify-content: center; padding: 16px; box-sizing: border-box; }
.uidmanual-root .payer-name-dialog { width: min(100%, 320px); background: #fff; border-radius: 8px; padding: 20px 16px 16px; box-sizing: border-box; }
.uidmanual-root .payer-name-title { margin: 0 0 12px; font-size: 18px; font-weight: 600; color: #f56c6c; }
.uidmanual-root .payer-name-text { margin: 0 0 12px; font-size: 14px; color: #303133; line-height: 1.5; }
.uidmanual-root .payer-name-input { width: 100%; box-sizing: border-box; height: 48px; padding: 0 12px; border: 1px solid #dcdfe6; border-radius: 4px; font-size: 20px; text-align: center; margin-bottom: 14px; }
.uidmanual-root .payer-name-confirm { width: 100%; height: 40px; border: 0; border-radius: 8px; background: #1677ff; color: #fff; font-size: 16px; cursor: pointer; }
.uidmanual-root .payer-name-confirm:disabled { opacity: 0.5; cursor: not-allowed; }
.uidmanual-root .payer-name-err { margin: 8px 0 0; font-size: 13px; color: #f56c6c; }
@media (max-width: 360px) {
  .uidmanual-root .amount { font-size: 30px; }
}
`
      document.head.appendChild(el)
    },
    async boot () {
      this.booting = true
      try {
        await this.refreshPage(true)
        this.startTimers()
      } finally {
        this.booting = false
      }
    },
    startTimers () {
      this.clearTimers()
      this.tickTimer = setInterval(() => {
        this.nowMs = Date.now()
        if (this.expireAtMillis && this.nowMs >= this.expireAtMillis && !this.orderPaid) {
          this.expired = true
        }
      }, 1000)
      this.pollTimer = setInterval(() => {
        if (this.orderPaid || this.invalidToken || this.pageLoadFailed || !this.payerNameFilled) return
        this.refreshPage(false, true)
      }, POLL_MS)
    },
    async refreshPage (isFirst, statusPoll) {
      try {
        const data = await fetchUidManualCashierPage(this.orderId, this.token, !!statusPoll)
        if (!data) {
          if (isFirst) {
            this.pageLoadFailed = true
            this.failReason = '收银台返回空数据'
          }
          return
        }
        this.pageLoadFailed = false
        this.invalidToken = !!data.invalidToken
        this.expired = !!data.expired
        this.orderPaid = !!data.orderPaid
        this.brandTitle = (data.brandTitle && String(data.brandTitle).trim()) || this.brandTitle
        this.brandSub = (data.brandSub && String(data.brandSub).trim()) || this.brandSub
        this.amountYuan = data.amountYuan || this.amountYuan || '--'
        this.amountHint = (data.amountHint && String(data.amountHint).trim()) || this.amountHint || AMOUNT_HINT
        this.mchOrderNo = data.mchOrderNo || this.mchOrderNo
        this.createTime = data.createTime || this.createTime
        this.hint = data.hint || this.hint
        this.failReason = data.failReason || this.failReason
        this.expireAtMillis = data.expireAtMillis != null ? Number(data.expireAtMillis) : this.expireAtMillis
        this.payerNameFilled = !!data.payerNameFilled
        if (typeof data.scanPay === 'boolean') this.scanPay = data.scanPay
        if (data.qrImage) this.qrImage = data.qrImage
        if (data.scanJumpUrl) this.scanJumpUrl = data.scanJumpUrl
        if (data.manualAlipayAccount != null) this.manualAlipayAccount = data.manualAlipayAccount
        if (this.brandTitle) document.title = this.brandTitle
        this.ensureScanPaying()
      } catch (e) {
        if (isFirst) {
          this.pageLoadFailed = true
          this.failReason = (e && (e.msg || e.message)) || '收银台接口请求失败，请确认 payment 已发布'
        }
      }
    },
    onConfirmPayerName () {
      const name = String(this.payerNameInput || '').trim()
      if (name.length < 2) {
        this.payerNameError = '请输入付款人真实姓名'
        return
      }
      if (this.payerNameSubmitting) return
      this.payerNameError = ''
      this.payerNameSubmitting = true
      submitUidManualPayerName(this.orderId, this.token, name)
        .then(() => this.refreshPage(false))
        .then(() => {
          this.payerNameSubmitting = false
          if (!this.payerNameFilled) {
            this.payerNameError = '姓名未写入订单，请重试'
          }
        })
        .catch((err) => {
          this.payerNameSubmitting = false
          this.payerNameError = (err && (err.message || err.msg)) ? String(err.message || err.msg) : '提交失败，请重试'
        })
    },
    ensureScanPaying () {
      if (!this.scanPay || !this.canPay || this.scanReady || this.scanEnsuring || this.scanTries >= 3) return
      this.scanEnsuring = true
      this.scanTries += 1
      executeUnifiedPay(this.orderId, this.token)
        .then(() => { this.scanReady = true })
        .catch(() => {
          if (this.scanTries >= 3) this.showToast('订单未进入支付中，请刷新页面')
        })
        .then(() => { this.scanEnsuring = false })
    },
    onJump () {
      if (this.jumping || !this.canPay) return
      this.jumping = true
      executeUnifiedPay(this.orderId, this.token)
        .then((data) => {
          const payUrl = data && (data.payUrl || data.channelPayUrl || data.codeUrl)
          if (payUrl) {
            window.location.href = payUrl
            return
          }
          this.showToast((data && data.message) || '打开支付宝失败，请重试')
          this.jumping = false
        })
        .catch((err) => {
          this.jumping = false
          this.showToast((err && (err.message || err.msg)) || '打开支付宝失败，请重试')
        })
    },
    copyText (text) {
      const v = String(text || '').trim()
      if (!v || !this.payerNameFilled) return
      const ok = () => this.showToast('已复制')
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(v).then(ok).catch(() => this.copyFallback(v, ok))
      } else {
        this.copyFallback(v, ok)
      }
    },
    copyFallback (v, ok) {
      const ta = document.createElement('textarea')
      ta.value = v
      ta.setAttribute('readonly', 'readonly')
      ta.style.position = 'fixed'
      ta.style.left = '-9999px'
      document.body.appendChild(ta)
      ta.select()
      try { document.execCommand('copy'); ok() } catch (e) { /* ignore */ }
      document.body.removeChild(ta)
    },
    showToast (msg) {
      this.toastText = msg || '已复制'
      this.toastVisible = true
      if (this.toastTimer) clearTimeout(this.toastTimer)
      this.toastTimer = setTimeout(() => { this.toastVisible = false }, 1500)
    }
  }
}
</script>
